/*
 * Through Your Eyes entry form (sections/through-your-eyes-entry.liquid).
 * Sends one "Through Your Eyes Entry" event to Klaviyo's client API with the
 * public key, then swaps the form for the thank-you on the same page (TYE-03..06).
 * Every visitor-facing string comes from the section's data-tye-copy block and is
 * written with textContent only.
 */
(() => {
  const KLAVIYO_REVISION = '2026-07-15';
  const METRIC_NAME = 'Through Your Eyes Entry';
  const SOURCE = 'through-your-eyes-page';
  const TIMEOUT_MS = 10000;
  // People type "jane." on the way to "jane.doe": the trailing full stop message waits (D-07).
  const EDGE_DOT_PAUSE_MS = 800;
  const INSTAGRAM = 'https://www.instagram.com/';

  // D-07 rules live in assets/through-your-eyes-handle.js (loaded first, unit-tested in node).
  function rules() {
    return window.BelantaraTyeHandle;
  }

  function normaliseEmail(value) {
    return String(value || '')
      .trim()
      .toLowerCase();
  }

  function newEntryId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    // Fallback for older browsers: RFC 4122 v4 shape from getRandomValues.
    const bytes = window.crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // One POST to Klaviyo's client API, given up after TIMEOUT_MS.
  function postInit(body) {
    const init = {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
      body: JSON.stringify(body),
    };
    if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
      init.signal = AbortSignal.timeout(10000); // TIMEOUT_MS
    }
    return init;
  }

  class ThroughYourEyesEntry extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.publicApiKey = this.dataset.klaviyoPublicApiKey || '';
      this.marketingListId = this.dataset.marketingListId || '';
      this.copy = {};
      const copyNode = this.querySelector('[data-tye-copy]');
      if (copyNode) {
        try {
          this.copy = JSON.parse(copyNode.textContent || '{}');
        } catch {
          this.copy = {};
        }
      }

      this.form = this.querySelector('[data-tye-form]');
      this.intro = this.querySelector('[data-tye-intro]');
      this.thanks = this.querySelector('[data-tye-thanks]');
      this.banner = this.querySelector('[data-tye-banner]');
      this.submitButton = this.form ? this.form.querySelector('[type="submit"]') : null;
      this.checkLine = this.querySelector('[data-tye-check-line]');
      this.checkLink = this.querySelector('[data-tye-check-link]');
      this.honeypot = this.querySelector('[data-tye-hp]');
      this.entryId = null;
      this.sending = false;
      this.edgeDotTimer = null;
      // typed: the visitor has changed the field; touched: its message is now shown live.
      this.typed = { email: false, handle: false };
      this.touched = { email: false, handle: false };

      this.order = ['email', 'handle'];
      this.controls = { email: this.field('email'), handle: this.field('instagram_handle') };
      this.order.forEach((key) => this.setUpControl(key, signal));

      // An inert Enter must post nothing: stop the tap before the form sees it.
      if (this.submitButton) {
        this.submitButton.addEventListener('click', (event) => this.onSubmitTap(event), { capture: true, signal });
      }
      if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event), { signal });
      const again = this.querySelector('[data-tye-again] button');
      if (again) again.addEventListener('click', () => this.showForm(), { signal });

      this.updateCheckLink();
      this.updateGate();
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      this.clearEdgeDotTimer();
    }

    field(name) {
      return this.form ? this.form.querySelector(`[name="${name}"]`) : null;
    }

    errorLine(key) {
      return this.querySelector(`[data-tye-error-for="${key}"]`);
    }

    setUpControl(key, signal) {
      const control = this.controls[key];
      if (!control) return;
      // Attributes the input snippet cannot emit.
      control.setAttribute('spellcheck', 'false');
      if (key === 'email') control.setAttribute('inputmode', 'email');
      const ids = (control.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      const line = this.errorLine(key);
      if (line && line.id && !ids.includes(line.id)) ids.push(line.id);
      if (key === 'handle' && this.checkLine && this.checkLine.id && !ids.includes(this.checkLine.id)) {
        ids.push(this.checkLine.id);
      }
      if (ids.length) control.setAttribute('aria-describedby', ids.join(' '));

      control.addEventListener('input', () => this.onInput(key), { signal });
      control.addEventListener('blur', (event) => this.onBlur(key, event), { signal });
    }

    copyText(key, vars) {
      const raw = this.copy[key];
      if (typeof raw !== 'string') return key;
      // Shopify's t filter HTML-escapes the copy (can't -> can&#39;t); it is shown with textContent,
      // so turn the entities back into characters first.
      const text = new DOMParser().parseFromString(raw, 'text/html').documentElement.textContent || '';
      return text.replace(/\{(\w+)\}/g, (match, name) => (vars && name in vars ? String(vars[name]) : match));
    }

    cleanedHandle() {
      return rules().cleanHandle(this.controls.handle ? this.controls.handle.value : '');
    }

    // The error key for a field, or null when it is fine.
    errorFor(key) {
      if (key === 'email') return rules().emailError(this.controls.email ? this.controls.email.value : '');
      return rules().handleError(this.cleanedHandle());
    }

    isValid() {
      return this.errorFor('email') === null && this.errorFor('handle') === null;
    }

    showError(key, errorKey) {
      const control = this.controls[key];
      const line = this.errorLine(key);
      if (!control || !line) return;
      control.setAttribute('aria-invalid', 'true');
      const text = line.querySelector('[data-tye-error-text]');
      if (text) text.textContent = this.copyText(`error_${errorKey}`);
      line.hidden = false;
    }

    clearError(key) {
      const control = this.controls[key];
      const line = this.errorLine(key);
      if (control) control.removeAttribute('aria-invalid');
      if (!line) return;
      const text = line.querySelector('[data-tye-error-text]');
      if (text) text.textContent = '';
      line.hidden = true;
    }

    // Shows or clears a field's message now, with no pause.
    reveal(key) {
      const errorKey = this.errorFor(key);
      if (errorKey) this.showError(key, errorKey);
      else this.clearError(key);
    }

    clearEdgeDotTimer() {
      if (this.edgeDotTimer) {
        clearTimeout(this.edgeDotTimer);
        this.edgeDotTimer = null;
      }
    }

    onInput(key) {
      this.typed[key] = true;
      if (key === 'email') {
        // Email: live only after its first blur or an inert Enter tap.
        if (this.touched.email) this.reveal('email');
      } else {
        // Handle: live on every keystroke against the cleaned value.
        this.touched.handle = true;
        this.clearEdgeDotTimer();
        const cleaned = this.cleanedHandle();
        const errorKey = rules().handleError(cleaned);
        if (errorKey === 'handle_edge_dot' && cleaned.endsWith('.')) {
          this.clearError('handle');
          this.edgeDotTimer = setTimeout(() => {
            this.edgeDotTimer = null;
            this.reveal('handle');
          }, EDGE_DOT_PAUSE_MS);
        } else if (errorKey) {
          this.showError('handle', errorKey);
        } else {
          this.clearError('handle');
        }
        this.updateCheckLink();
      }
      this.updateGate();
    }

    onBlur(key, event) {
      if (key === 'handle' && this.controls.handle) {
        // The field shows the one clean form that is saved (D-07).
        this.controls.handle.value = this.cleanedHandle();
      }
      // Moving to Enter: its tap validates. Showing a message now would shift the button
      // under the pointer and swallow the click.
      if (event.relatedTarget && event.relatedTarget === this.submitButton) return;
      // Tabbing through an untouched empty field is not a mistake.
      if (!this.typed[key] && !this.touched[key]) return;
      this.touched[key] = true;
      if (key === 'handle') this.clearEdgeDotTimer();
      this.reveal(key);
    }

    updateGate() {
      if (!this.submitButton) return;
      if (this.isValid()) this.submitButton.removeAttribute('aria-disabled');
      else this.submitButton.setAttribute('aria-disabled', 'true');
    }

    // D-08: a link to the profile, built only from a handle that passed the rules
    // ([a-z0-9._]). No check that the account exists or is public.
    updateCheckLink() {
      if (!this.checkLine || !this.checkLink) return;
      const cleaned = this.cleanedHandle();
      if (rules().handleError(cleaned) !== null) {
        this.checkLine.hidden = true;
        this.checkLink.removeAttribute('href');
        this.checkLink.textContent = '';
        return;
      }
      this.checkLink.href = `${INSTAGRAM}${encodeURIComponent(cleaned)}/`;
      this.checkLink.textContent = this.copyText('check_link', { handle: cleaned });
      const newTab = document.createElement('span');
      newTab.className = 'sr-only';
      newTab.textContent = ` ${this.copyText('new_tab')}`;
      this.checkLink.appendChild(newTab);
      this.checkLine.hidden = false;
    }

    // Inert Enter: send nothing, show every message, focus the first field to fix.
    blockInert(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.clearEdgeDotTimer();
      let first = null;
      this.order.forEach((key) => {
        this.touched[key] = true;
        this.reveal(key);
        if (!first && this.errorFor(key)) first = this.controls[key];
      });
      if (first) first.focus();
    }

    onSubmitTap(event) {
      if (this.submitButton && this.submitButton.getAttribute('aria-disabled') === 'true') this.blockInert(event);
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.sending) return;
      if (!this.isValid()) {
        this.blockInert(event);
        this.updateGate();
        return;
      }

      const email = normaliseEmail(this.controls.email.value);
      const handle = this.cleanedHandle();
      const consent = this.field('marketing_consent');
      const ticked = Boolean(consent && consent.checked);

      // Bots fill every field: send nothing and show the same thank-you, so they learn nothing.
      if (this.honeypot && this.honeypot.value !== '') {
        this.showThanks();
        return;
      }

      // One id per entry: kept after a failure so the retry is recorded once (Klaviyo keeps the
      // first event with a given unique_id), cleared after success so the next entry is its own.
      if (!this.entryId) this.entryId = newEntryId();

      this.setSending(true);
      if (this.banner) this.banner.hidden = true;

      try {
        await this.sendEntry({ email, handle, ticked, entryId: this.entryId });
        this.entryId = null;
        this.setSending(false);
        this.showThanks();
      } catch {
        // No automatic retry: the visitor taps Enter again with every value kept.
        this.setSending(false);
        if (this.banner) this.banner.hidden = false;
      }
    }

    // Single flight: the busy button and readonly fields stop a second send while one is out.
    setSending(on) {
      this.sending = on;
      if (this.submitButton) {
        if (on) this.submitButton.setAttribute('aria-busy', 'true');
        else this.submitButton.removeAttribute('aria-busy');
      }
      this.order.forEach((key) => {
        const control = this.controls[key];
        if (!control) return;
        if (on) control.setAttribute('readonly', '');
        else control.removeAttribute('readonly');
      });
    }

    async sendEntry({ email, handle, ticked, entryId }) {
      if (!this.publicApiKey) throw new Error('Missing Klaviyo public API key.');
      const now = new Date().toISOString();
      const body = {
        data: {
          type: 'event',
          attributes: {
            metric: { data: { type: 'metric', attributes: { name: METRIC_NAME } } },
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  properties: { instagram_handle: handle, tye_entry_date: now },
                },
              },
            },
            properties: {
              instagram_handle: handle,
              entry_date: now,
              marketing_opt_in: ticked,
              source: SOURCE,
            },
            time: now,
            unique_id: entryId,
          },
        },
      };

      const response = await fetch(
        `https://a.klaviyo.com/client/events?company_id=${encodeURIComponent(this.publicApiKey)}`,
        postInit(body),
      );
      if (!response.ok) throw new Error(`Klaviyo entry event failed with ${response.status}.`);
      // D-01/D-13: the ticked box is the only route to a list, and only after the entry itself.
      if (ticked) await this.subscribe(email);
    }

    // Marketing consent to the Master List. The subscriptions object must always be present:
    // without it Klaviyo would record a different consent (RESEARCH Pitfall 1).
    async subscribe(email) {
      if (!this.publicApiKey || !this.marketingListId) {
        throw new Error('Missing Klaviyo marketing consent configuration.');
      }
      const body = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Through Your Eyes',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  properties: { marketing_consent_source: 'through_your_eyes' },
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                },
              },
            },
          },
          relationships: { list: { data: { type: 'list', id: this.marketingListId } } },
        },
      };
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(this.publicApiKey)}`,
        postInit(body),
      );
      if (!response.ok) throw new Error(`Klaviyo subscription failed with ${response.status}.`);
    }

    showThanks() {
      if (this.banner) this.banner.hidden = true;
      if (this.form) this.form.hidden = true;
      if (this.intro) this.intro.hidden = true;
      if (!this.thanks) return;
      this.thanks.hidden = false;
      const heading = this.thanks.querySelector('[data-tye-thanks-heading]');
      if (!heading) return;
      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      heading.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      heading.focus({ preventScroll: true });
    }

    showForm() {
      if (this.thanks) this.thanks.hidden = true;
      if (this.intro) this.intro.hidden = false;
      if (!this.form) return;
      this.form.hidden = false;
      const handleField = this.controls.handle;
      if (handleField) {
        handleField.value = '';
        this.typed.handle = false;
        this.touched.handle = false;
        this.clearEdgeDotTimer();
        this.clearError('handle');
        this.updateCheckLink();
        this.updateGate();
        handleField.focus();
      }
    }
  }

  if (!customElements.get('through-your-eyes-entry')) {
    customElements.define('through-your-eyes-entry', ThroughYourEyesEntry);
  }
})();
